/* eslint-disable @typescript-eslint/indent */
import * as React from 'react';
import { ExternshipApi } from '@api/Externships.api';

export type FetchExternshipStartDates = {
  externshipDates: Date[];
  externshipDatesByYear: { [key: string]: Date[] };
  years: string[];
  savedExternshipDate?: Date;
  loaded: boolean;
  loading: boolean;
};

export type DateFetchType = 'START_DATES' | 'GRAD_DATES';

export const useFetchExternshipDates = (
  dateFetchType: DateFetchType | undefined = 'START_DATES'
): FetchExternshipStartDates => {
  const [externshipDates, setExternshipDates] = React.useState<Date[]>([]);
  const [externshipDatesByYear, setExternshipDatesByYear] = React.useState<{
    [key: string]: Date[];
  }>({});
  const [years, setYears] = React.useState<string[]>([]);

  const [savedExternshipDate, setSavedExternshipDate] = React.useState<Date>();

  const [loaded, setLoaded] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const getStartDateMapByYearAndSetYears = (
    dates: Date[]
  ): { [key: string]: Date[] } => {
    const startDateMapByYear = {};

    dates.forEach((date) => {
      const year = date.getFullYear();
      if (startDateMapByYear[year]) {
        startDateMapByYear[year].push(date);
      } else {
        startDateMapByYear[year] = [date];
      }
    });

    const years = Object.keys(startDateMapByYear).sort();
    setYears(years);

    return startDateMapByYear;
  };

  const processAndSaveExternshipDates = (
    response: any,
    shouldUpdateStates: boolean,
    shouldUpdateCache: boolean,
  ): void => {
    if (shouldUpdateStates) {
      const dates = response.dates.map((d: string) => new Date(d));
      setExternshipDates(dates);
      setExternshipDatesByYear(getStartDateMapByYearAndSetYears(dates));
      
      if (response.externshipTrackerDatePreference) {
        setSavedExternshipDate(new Date(response.externshipTrackerDatePreference));
      }
    }
    
    if (shouldUpdateCache) {
      const now = new Date().getTime();
      localStorage.setItem('externshipStartDates', JSON.stringify(response));
      localStorage.setItem('externshipStartDatesTimestamp', now.toString());
    }
  };

  const fetchCachedExternshipStartDates = async (): Promise<void> => {
    try {
      setLoading(true);
      
      // Check for cached data first
      const cachedData = localStorage.getItem('externshipStartDates');
      const cacheTimestamp = localStorage.getItem('externshipStartDatesTimestamp');
      const now = new Date().getTime();
      
      // Use cache if it exists (regardless of age)
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        const dates = parsedData.dates.map(d => new Date(d));
        setExternshipDates(dates);
        setExternshipDatesByYear(getStartDateMapByYearAndSetYears(dates));
        
        if (parsedData.externshipTrackerDatePreference) {
          setSavedExternshipDate(new Date(parsedData.externshipTrackerDatePreference));
        }
        
        // If cache is older than 24 hours, refresh silently in the background
        if (!cacheTimestamp || (now - parseInt(cacheTimestamp)) > 24 * 60 * 60 * 1000) {
          refreshExternshipStartDatesInBackground();
        }
      } else {
        // No cache, fetch fresh data
        await fetchExternshipStartDates();
      }
    } catch (error: any) {
      console.error(
        'Error for useFetchExternshipDates -> ExternshipApi.getExternshipStartDates()',
        error
      );
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  const fetchExternshipStartDates = async (): Promise<void> => {
    try {
      const response = await ExternshipApi.getExternshipStartDates();
      processAndSaveExternshipDates(response, true, true);
    } catch (error: any) {
      console.error('Error fetching fresh externship dates', error);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  const refreshExternshipStartDatesInBackground = async (): Promise<void> => {
    try {
      const response = await ExternshipApi.getExternshipStartDates();
      
      // Compare new data with current data to check for differences
      const newDates = response.dates.map(d => new Date(d));
      const cachedData = localStorage.getItem('externshipStartDates');
      
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        const currentDates = parsedData.dates.map(d => new Date(d));
        
        // Check if the dates are different
        const datesChanged = newDates.length !== currentDates.length || 
          newDates.some((date, index) => date.getTime() !== new Date(currentDates[index]).getTime());
        
        // Update state only if data changed
        processAndSaveExternshipDates(response, datesChanged, true);
      } else {
        processAndSaveExternshipDates(response, true, true);
      }
    } catch (error: any) {
      console.error('Error refreshing externship dates in background', error);
      // Silently fail in background refresh
    }
  };

  const getGradDateMapByYearAndSetYears = (
    dates: Date[]
  ): { [key: string]: Date[] } => {
    const gradDateMapByYear = {};

    dates.forEach((date) => {
      const year = date.getFullYear();
      if (gradDateMapByYear[year]) {
        gradDateMapByYear[year].push(date);
      } else {
        gradDateMapByYear[year] = [date];
      }
    });

    const years = Object.keys(gradDateMapByYear).sort();
    setYears(years);

    return gradDateMapByYear;
  };

  const fetchExternshipGradDates = async (): Promise<void> => {
    try {
      setLoading(true);
      const res = await ExternshipApi.getExternshipGradDates();

      const dates = res.map((d) => new Date(d));
      setExternshipDates(dates);
      setExternshipDatesByYear(getGradDateMapByYearAndSetYears(dates));
    } catch (error: any) {
      console.error(
        'Error for useFetchExternshipDates -> ExternshipApi.getExternshipGradDates()',
        error
      );
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  React.useEffect(() => {
    if (dateFetchType === 'START_DATES') {
      fetchCachedExternshipStartDates();
    } else if (dateFetchType === 'GRAD_DATES') {
      fetchExternshipGradDates();
    }
  }, [dateFetchType]);

  return {
    externshipDates,
    externshipDatesByYear,
    savedExternshipDate,
    years,
    loaded,
    loading
  };
};
